/**
 * Sticky buyer shipping destination (country, then US state).
 *
 * Session/device-wide via localStorage. SSR-safe: never touches `window` at
 * module load, and load/save no-op when storage is missing or blocked.
 */

import { useEffect, useState } from 'react';

import { toCountryCode, toUsStateCode } from '../config/usStates';

export const SHIPPING_DESTINATION_STORAGE_KEY = 'r2r.shippingDestination';

const getLocalStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null;
  }
  return window.localStorage;
};

/**
 * @param {{ country?: string, state?: string }|null|undefined} value
 * @returns {{ country: string|null, state: string|null }}
 */
export const normalizeShippingDestination = (value = {}) => {
  const country = toCountryCode(value?.country);
  const state = country === 'US' ? toUsStateCode(value?.state) : null;
  return { country, state };
};

/**
 * @returns {{ country: string, state: string|null }|null}
 */
export const loadShippingDestination = () => {
  const storage = getLocalStorage();
  if (!storage) {
    return null;
  }

  try {
    const raw = storage.getItem(SHIPPING_DESTINATION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }
    const normalized = normalizeShippingDestination(parsed);
    return normalized.country ? normalized : null;
  } catch (e) {
    return null;
  }
};

/**
 * Whether checkout country/state is complete enough to update sticky dest.
 * Non-US: country alone. US: mapped state code required.
 *
 * @param {{ country?: string, state?: string }|null|undefined} value
 * @returns {boolean}
 */
export const isCheckoutShippingDestinationSet = (value = {}) => {
  const country = toCountryCode(value?.country);
  if (!country) {
    return false;
  }
  if (country !== 'US') {
    return true;
  }
  return !!toUsStateCode(value?.state);
};

/**
 * True when `candidate` is strictly less specific than `existing` (same country,
 * US with state beats US country-only).
 *
 * @param {{ country: string|null, state: string|null }} candidate
 * @param {{ country: string, state: string|null }} existing
 * @returns {boolean}
 */
const isLessSpecificThan = (candidate, existing) => {
  if (!existing?.country || !candidate?.country) {
    return false;
  }
  if (candidate.country !== existing.country) {
    return false;
  }
  if (candidate.country === 'US') {
    return !!existing.state && !candidate.state;
  }
  return false;
};

/**
 * Persist sticky dest only when checkout country/state is set, and never replace
 * a more specific stored destination with a less specific one.
 *
 * @param {{ country?: string, state?: string }|null|undefined} value
 * @returns {{ country: string|null, state: string|null }}
 */
export const saveShippingDestinationIfSet = (value = {}) => {
  if (!isCheckoutShippingDestinationSet(value)) {
    return loadShippingDestination() ?? normalizeShippingDestination(value);
  }

  const normalized = normalizeShippingDestination(value);
  const existing = loadShippingDestination();
  if (existing && isLessSpecificThan(normalized, existing)) {
    return existing;
  }

  return saveShippingDestination(value);
};

/**
 * @param {{ country?: string, state?: string }|null|undefined} value
 * @returns {{ country: string|null, state: string|null }}
 */
export const saveShippingDestination = (value = {}) => {
  const normalized = normalizeShippingDestination(value);
  const storage = getLocalStorage();
  if (!storage) {
    return normalized;
  }

  try {
    if (!normalized.country) {
      storage.removeItem(SHIPPING_DESTINATION_STORAGE_KEY);
    } else {
      storage.setItem(SHIPPING_DESTINATION_STORAGE_KEY, JSON.stringify(normalized));
    }
  } catch (e) {
    /* ignore quota / privacy errors */
  }

  return normalized;
};

/**
 * Client-hydrated sticky destination. Starts null on the server (and first
 * client paint) so SSR markup stays consistent, then reads localStorage.
 *
 * @returns {[
 *   { country: string, state: string|null }|null,
 *   Function,
 *   boolean
 * ]} destination, updateDestination, destinationHydrated
 */
export const useShippingDestination = () => {
  const [destination, setDestination] = useState(() =>
    typeof window !== 'undefined' ? loadShippingDestination() : null
  );
  const [destinationHydrated, setDestinationHydrated] = useState(
    () => typeof window !== 'undefined'
  );

  useEffect(() => {
    if (!destinationHydrated) {
      setDestination(loadShippingDestination());
      setDestinationHydrated(true);
    }
  }, [destinationHydrated]);

  const updateDestination = next => {
    const saved = saveShippingDestination(next);
    setDestination(saved.country ? saved : null);
    return saved;
  };

  return [destination, updateDestination, destinationHydrated];
};
